import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  queryStatus,
  ShowToast,
  TaskStatus,
  PatternsErrorHandler,
} from '../../model';
import { Translate } from '../../services';
import { ModalState } from '../ModalState';
import { ConditionalModalState } from '../ConditionalModalState';
import { CreateExportAsyncJobRequest } from '@wix/bex-utils/@wix/ambassador-fedinfra-exportservice-v1-export-async-job/types';
import { ParseCsvResult, parseCsv } from './parseCsv';
import set from 'lodash/set';

export interface ImportCollection {
  clearResultAndMoveToStart(params?: { force?: boolean }): void;
}

export interface BulkUploadError {
  rowIndex: number;
  message?: string;
  code?: string;
}

export interface BulkUploadResults {
  succeeded: number;
  failed: number;
  errors: BulkUploadError[];
  insertedIds?: string[];
}

export type BulkUploadItemsFn = (
  items: Record<string, unknown>[],
) => Promise<BulkUploadResults>;

export interface BackupResult {
  restore(signal: AbortSignal): Promise<void>;
}

export type BackupFn = (signal: AbortSignal) => Promise<BackupResult | void>;

export type ImportField = NonNullable<
  CreateExportAsyncJobRequest['fields']
>[number];

export interface ImportStateProps {
  fields: CreateExportAsyncJobRequest['fields'];
  bulkUploadItems: BulkUploadItemsFn;
  backup?: BackupFn;
  collection: ImportCollection;
  importModalState: ModalState;
  translate: Translate;
  errorHandler: PatternsErrorHandler;
  showToast?: ShowToast;
  batchSize?: number;
}

export type ImportStep = 'upload' | 'configuration' | 'progress';

const DEFAULT_BATCH_SIZE = 100;

function normalize(value: string) {
  return value.trim().toLowerCase();
}

function toMappedRows(
  rows: string[][],
  headers: string[],
  mappingByHeader: Record<string, string | null | undefined>,
) {
  return rows.map((row) => {
    const mapped: Record<string, unknown> = {};

    headers.forEach((header, index) => {
      const mappedField = mappingByHeader[header];
      if (mappedField == null) {
        return;
      }

      set(mapped, mappedField, row[index] ?? '');
    });

    return mapped;
  });
}

export class ImportState {
  readonly fields: ImportField[];
  readonly bulkUploadItems: BulkUploadItemsFn;
  readonly backup?: BackupFn;
  readonly collection: ImportCollection;
  readonly importModalState: ModalState;
  readonly translate: Translate;
  readonly errorHandler: PatternsErrorHandler;
  readonly showToast?: ShowToast;
  readonly batchSize: number;
  readonly discardChangesModal = new ConditionalModalState<'goBack' | 'close'>();

  currentStep: ImportStep = 'upload';
  importStatus: TaskStatus<BulkUploadResults, unknown> = {
    ...queryStatus.idle,
  };
  parseError: string | null = null;
  fileName = '';
  csvHeaders: string[] = [];
  csvRows: string[][] = [];
  mappingByHeader: Record<string, string | null> = {};
  processedCount = 0;
  totalCount = 0;
  result: BulkUploadResults = {
    succeeded: 0,
    failed: 0,
    errors: [],
  };
  isBackingUp = false;
  isRestoring = false;
  private restoreFn: ((signal: AbortSignal) => Promise<void>) | null = null;
  private _cancelled = false;
  private _abortController: AbortController | null = null;

  constructor(props: ImportStateProps) {
    this.fields = (props.fields ?? []) as ImportField[];
    this.bulkUploadItems = props.bulkUploadItems;
    this.backup = props.backup;
    this.collection = props.collection;
    this.importModalState = props.importModalState;
    this.translate = props.translate;
    this.errorHandler = props.errorHandler;
    this.showToast = props.showToast;
    this.batchSize = props.batchSize ?? DEFAULT_BATCH_SIZE;

    makeObservable(this, {
      currentStep: observable.ref,
      importStatus: observable.ref,
      parseError: observable.ref,
      fileName: observable.ref,
      csvHeaders: observable.ref,
      csvRows: observable.ref,
      mappingByHeader: observable.ref,
      processedCount: observable.ref,
      totalCount: observable.ref,
      result: observable.ref,
      isBackingUp: observable.ref,
      isRestoring: observable.ref,
      hasParsedRows: computed,
      hasMappings: computed,
      selectedFieldIds: computed,
      mappedRows: computed,
      onUploadContinue: action.bound,
      goToUpload: action.bound,
      setMapping: action.bound,
      close: action.bound,
      onFileSelected: action.bound,
      startImport: action.bound,
      reset: action.bound,
    });
  }

  init() {
    return () => undefined;
  }

  get hasParsedRows() {
    return this.csvHeaders.length > 0 && this.csvRows.length > 0;
  }

  get hasMappings() {
    return Object.values(this.mappingByHeader).some((value) => value != null);
  }

  get selectedFieldIds(): Set<string> {
    return new Set(
      Object.values(this.mappingByHeader).filter(
        (v): v is string => v != null,
      ),
    );
  }

  get mappedRows() {
    return toMappedRows(this.csvRows, this.csvHeaders, this.mappingByHeader);
  }

  reset() {
    this._abortController?.abort();
    this._abortController = null;
    this.currentStep = 'upload';
    this.importStatus = {
      ...queryStatus.idle,
    };
    this.parseError = null;
    this.fileName = '';
    this.csvHeaders = [];
    this.csvRows = [];
    this.mappingByHeader = {};
    this.processedCount = 0;
    this.totalCount = 0;
    this.result = {
      succeeded: 0,
      failed: 0,
      errors: [],
    };
    this.isBackingUp = false;
    this.isRestoring = false;
    this.restoreFn = null;
    this._cancelled = false;
  }

  async close() {
    if (this.currentStep === 'progress') {
      this._cancelled = true;
      this._abortController?.abort();

      if (!this.isBackingUp) {
        await this._restoreBackup();
      }

      this.collection.clearResultAndMoveToStart({
        force: true,
      });
    }

    this.importModalState.close();
  }

  private async _restoreBackup() {
    const restoreFn = this.restoreFn;
    if (!restoreFn) {
      return;
    }

    const restoreController = new AbortController();
    this._abortController = restoreController;

    this.isRestoring = true;
    try {
      await restoreFn(restoreController.signal);
    } catch (error) {
      console.error('Restore failed', error);
    }
    runInAction(() => {
      this.isRestoring = false;
      this.restoreFn = null;
    });
  }

  goToUpload() {
    this.currentStep = 'upload';
  }

  async discardAndGoBack() {
    this.discardChangesModal.open('goBack');
    const { result } = await this.discardChangesModal.modal.promise();
    if (result === 'confirmed') {
      this.goToUpload();
    }
  }

  async discardAndClose() {
    this.discardChangesModal.open('close');
    const { result } = await this.discardChangesModal.modal.promise();
    if (result === 'confirmed') {
      this.close();
    }
  }

  onUploadContinue() {
    if (this.hasParsedRows) {
      this.currentStep = 'configuration';
    }
  }

  private getDefaultMapping(headers: string[]) {
    const mapping: Record<string, string | null> = {};
    const fields = this.fields.map((field) => ({
      id: field.id ?? '',
      header: field.header ?? '',
    }));

    headers.forEach((header) => {
      const normalizedHeader = normalize(header);
      const matched = fields.find(
        (field) =>
          normalize(field.id) === normalizedHeader ||
          normalize(field.header) === normalizedHeader,
      );

      mapping[header] = matched?.id || null;
    });

    return mapping;
  }

  private applyParsedCsv(fileName: string, parsedCsv: ParseCsvResult) {
    this.fileName = fileName;
    this.parseError = null;
    this.csvHeaders = parsedCsv.headers;
    this.csvRows = parsedCsv.rows;
    this.mappingByHeader = this.getDefaultMapping(parsedCsv.headers);
    this.currentStep = 'configuration';
  }

  async onFileSelected(file: File | null) {
    if (file == null) {
      return;
    }

    try {
      const content = await file.text();
      const parsedCsv = parseCsv(content);

      if ('message' in parsedCsv) {
        runInAction(() => {
          this.parseError = parsedCsv.message;
          this.fileName = file.name;
          this.csvHeaders = [];
          this.csvRows = [];
          this.mappingByHeader = {};
          this.currentStep = 'upload';
        });
        return;
      }

      runInAction(() => {
        this.applyParsedCsv(file.name, parsedCsv);
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.parseError = this.translate('cairo.import.parseFailed');
        this.currentStep = 'upload';
      });
    }
  }

  setMapping(header: string, fieldId: string | null) {
    this.mappingByHeader = {
      ...this.mappingByHeader,
      [header]: fieldId,
    };
  }

  private async _uploadBatches(batches: Record<string, unknown>[][]) {
    let processedRows = 0;

    for (const batch of batches) {
      if (this._cancelled) {
        break;
      }

      try {
        const batchResult = await this.bulkUploadItems(batch);

        this.result = {
          succeeded: this.result.succeeded + batchResult.succeeded,
          failed: this.result.failed + batchResult.failed,
          errors: [...this.result.errors, ...batchResult.errors],
        };
      } catch (error) {
        this.result = {
          succeeded: this.result.succeeded,
          failed: this.result.failed + batch.length,
          errors: this.result.errors,
        };

        break;
      }

      processedRows += batch.length;

      runInAction(() => {
        this.processedCount = Math.min(processedRows, this.totalCount);
      });
    }

    return this.result;
  }

  private async _runBackup(): Promise<boolean> {
    const backup = this.backup;
    if (!backup) {
      return false;
    }

    try {
      const result = await backup(this._abortController!.signal);
      if (this._cancelled) {
        return true;
      }
      if (result?.restore) {
        this.restoreFn = result.restore;
      }
    } catch (error) {
      if (this._cancelled) {
        return true;
      }
      runInAction(() => {
        this.isBackingUp = false;
        this.importStatus = {
          ...queryStatus.error,
          error,
        };
      });
      return true;
    }

    runInAction(() => {
      this.isBackingUp = false;
    });
    return false;
  }

  private _createRowsBatches(rows: Record<string, unknown>[]) {
    return rows.reduce<Record<string, unknown>[][]>((acc, row, index) => {
      if (index % this.batchSize === 0) {
        acc.push([]);
      }
      acc[acc.length - 1].push(row);
      return acc;
    }, []);
  }

  async startImport() {
    if (!this.hasMappings) {
      return;
    }

    this._cancelled = false;
    this._abortController = new AbortController();

    const rows = this.mappedRows;
    const totalCount = rows.length;
    const batches = this._createRowsBatches(rows);

    runInAction(() => {
      this.currentStep = 'progress';
      this.totalCount = totalCount;
      this.processedCount = 0;
      this.isBackingUp = !!this.backup;
    });

    const backupFailed = await this._runBackup();
    if (backupFailed) {
      return;
    }

    const promise = this._uploadBatches(batches);

    this.importStatus = {
      ...queryStatus.loading,
      promise,
    };

    try {
      await promise;
      runInAction(() => {
        if (this._cancelled) {
          return;
        }

        this.importStatus = {
          ...queryStatus.success,
          data: this.result,
        };

        if (this.result.failed === 0) {
          this.importModalState.close();
          this.collection.clearResultAndMoveToStart({ force: true });
        }
      });
    } catch (error) {
      runInAction(() => {
        this.importStatus = {
          ...queryStatus.error,
          error,
        };
      });
    }
  }
}
